import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  FiHome, FiUsers, FiBarChart2, FiSettings, FiLogOut, FiColumns, FiShield,
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import { logOut } from '../../services/authService';
import { useTranslation } from '../../i18n/useTranslation';
import '../../styles/topnav.css';

const ADMIN_EMAIL = 'mackayla.omalley00@gmail.com';

const NAV_ITEMS = [
  { Icon: FiHome,      labelKey: 'nav.myStimuli', path: '/dashboard' },
  { Icon: FiBarChart2, labelKey: 'nav.data',      path: '/data'      },
  { Icon: FiUsers,     labelKey: 'nav.profiles',  path: '/profiles'  },
  { Icon: FiSettings,  labelKey: 'nav.settings',  path: '/settings'  },
];

const ADMIN_NAV_ITEMS = [
  { Icon: FiShield,      labelKey: 'nav.adminDashboard', path: '/admin'      },
];

// Embedded SVG logo mark — 4-pointed star / compass rose in brand maroon
const LogoMark = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* four-pointed star */}
    <path
      d="M14 2 C14 2 15.2 8.8 18 11 C20.8 13.2 28 14 28 14 C28 14 20.8 14.8 18 17 C15.2 19.2 14 26 14 26 C14 26 12.8 19.2 10 17 C7.2 14.8 0 14 0 14 C0 14 7.2 13.2 10 11 C12.8 8.8 14 2 14 2Z"
      fill="#8B2635"
    />
    {/* small inner accent */}
    <circle cx="14" cy="14" r="2.5" fill="rgba(255,255,255,0.55)" />
  </svg>
);

export const TopNavBar = ({ onToggleNavMode }) => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = userData?.role === 'admin' || currentUser?.email === ADMIN_EMAIL;
  const navItems = isAdmin ? [...NAV_ITEMS, ...ADMIN_NAV_ITEMS] : NAV_ITEMS;

  const handleLogout = async () => {
    await logOut();
    navigate('/');
  };

  const name = userData?.displayName || currentUser?.email || '';
  const initials = (() => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  })();
  const displayName = userData?.displayName || currentUser?.email?.split('@')[0] || '';

  return (
    <header className="topnav-bar">
      <div className="topnav-brand">
        <Link to="/dashboard" className="topnav-logo-link">
          <LogoMark />
          <span className="topnav-wordmark">
            <span className="topnav-wordmark-line1">Stimuli</span>
            <span className="topnav-wordmark-line2">Central</span>
          </span>
        </Link>
      </div>

      <nav className="topnav-nav">
        {navItems.map(({ Icon, labelKey, path }) => (
          <NavLink
            key={path}
            to={path}
            end={path === '/dashboard'}
            className={({ isActive }) => `topnav-item${isActive ? ' active' : ''}`}
          >
            {Icon && <Icon size={16} className="topnav-item-icon" />}
            <span className="topnav-item-label">{t(labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="topnav-actions">
        <div className="topnav-user">
          <div className="topnav-avatar" title={name}>{initials}</div>
          <span className="topnav-username">{displayName}</span>
        </div>
        <button className="topnav-mode-btn" onClick={onToggleNavMode} title={t('nav.switchToSidebar')}>
          <FiColumns size={14} />
          <span>{t('nav.sidebarShort')}</span>
        </button>
        <button className="topnav-logout-btn" onClick={handleLogout}>
          <FiLogOut size={14} />
          <span>{t('nav.logOut')}</span>
        </button>
      </div>
    </header>
  );
};
